import Ajv from 'ajv'
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils'
import { CustomReport } from '../models/CustomReport'
import { CustomReportSchema } from '../models/CustomReportSchema'
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest'
import { CustomReportsService } from './CustomReportsService'
import { CustomReportsTypeGuardService } from './CustomReportsTypeGuardService'
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult'
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService'

export class CustomReportsJsonParsingResult extends CdnSchemaJsonParsingResult<CustomReport, CustomReportsJsonParsingInputType> {

    public name: string = "";

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            if ('customReports' in parsedValue && parsedValue.customReports?.reports) {
                this.parsedEntities = parsedValue.customReports.reports;
                this.name = parsedValue?.name;
                this.inputType = 'full';
            } else if ('reports' in parsedValue && Array.isArray(parsedValue.reports)) {
                this.parsedEntities = parsedValue.reports;
                this.name = parsedValue?.name;
                this.inputType = 'flat';
            } else if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.inputType = 'array';
            }
        }
    }

    public withParsedCustomReports(customReports: CustomReport[]): this {
        this.parsedEntities = customReports;
        return this;
    }
}

interface ICustomReportArrayStructure {
    customReports: {
        reports: CustomReport[];
    }
}

interface IFlatReportsArrayStructure {
    reports: CustomReport[];
}

type ICustomReportsArray = CustomReport[];

export type CustomReportsJsonParsingInputType = 'full' | 'flat' | 'array';

export class CustomReportsJsonParsingService extends SchemaJsonValidationService<CustomReport, CustomReportsJsonParsingInputType, CustomReportSchema> {

    static readonly injectableKey = 'CustomReportsJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof CustomReportsJsonParsingService).injectableKey;
    }

    private _fullReportsPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            parentExecutionId: {
                type: 'string'
            },
            name: {
                type: 'string'
            },
            customReport: {
                type: 'object',
                properties: {
                    reports: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['reports'],
                additionalProperties: true
            }
        },
        required: ['parentExecutionId', 'customReports'],
        additionalProperties: true
    });

    private _reportsArrayStructureSchemaValidator = new Ajv().compile<ICustomReportArrayStructure>({
        type: 'object',
        properties: {
            customReports: {
                type: 'object',
                properties: {
                    reports: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['reports'],
                additionalProperties: true
            }
        },
        required: ['customReports'],
        additionalProperties: true
    });

    private _flatReportsArrayStructureSchemaValidator = new Ajv().compile<IFlatReportsArrayStructure>({
        type: 'object',
        properties: {
            reports: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            }
        },
        required: ['reports'],
        additionalProperties: true
    });

    private _reportsArraySchemaValidator = new Ajv().compile<ICustomReportsArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    public tryParseEntitiesFromJson(value: string, customReportSchemas: Array<CustomReportSchema>): CustomReportsJsonParsingResult {
        if (customReportSchemas == null || customReportSchemas.length <= 0) throw new Error("customReportSchemas is required");

        var result = new CustomReportsJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }
        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseCustomReports(result, customReportSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;
    }

    private tryParseCustomReports(result: CustomReportsJsonParsingResult, customReportSchemas: Array<CustomReportSchema>): CustomReportsJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid customReports.parseValue encountered trying to validateCustomReportStructure.");

        const parsed = result.parsedValue;
        let customReports: CustomReport[] | undefined;

        // reset errors from any prior validation
        this._fullReportsPayloadStructureSchemaValidator.errors = null;
        this._reportsArrayStructureSchemaValidator.errors = null;
        this._flatReportsArrayStructureSchemaValidator.errors = null;
        this._reportsArraySchemaValidator.errors = null;

        if (this._fullReportsPayloadStructureSchemaValidator(parsed)) {
            customReports = parsed.customReports.reports;
        } else if (this._reportsArrayStructureSchemaValidator(parsed)) {
            customReports = parsed.customReports.reports;
        } else if (this._flatReportsArrayStructureSchemaValidator(parsed)) {
            customReports = parsed.reports;
        } else if (this._reportsArraySchemaValidator(parsed)) {
            customReports = parsed;
        } else {
            const errors = [
                ...(this._fullReportsPayloadStructureSchemaValidator.errors || []),
                ...(this._flatReportsArrayStructureSchemaValidator.errors || []),
                ...(this._reportsArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new CustomReportsJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = customReports.map(x => this.initializeEntityWithConfiguration(x, customReportSchemas));
            return new CustomReportsJsonParsingResult(true, parsed).withParsedCustomReports(initialized);
        } catch (err: any) {
            return new CustomReportsJsonParsingResult(false, parsed, [`${err.message}`]);
        }
    }

    public initializeEntityWithConfiguration(customReport: CustomReport, customReportSchemas: Array<CustomReportSchema>): CustomReport {
        if (customReport == null) throw new Error("customReport is required");
        if (!customReport.type) throw new Error("customReport.type is required")
        return this.initializeEntity(customReport, customReportSchemas, 1);
    }

    public initializeEntity(customReport: CustomReport, customReportSchemas: Array<CustomReportSchema>, configurationLevel: number): CustomReport {
        let initializedCustomReport = customReport;
        const customReportSchema = CustomReportsService.tryGetCustomReportSchemaByType(customReport.type, customReportSchemas);
        if (customReportSchema != null) {
            initializedCustomReport = CustomReportsService.createCustomReportFromCustomReportSchema(customReportSchema);
            if (customReport.description != null) initializedCustomReport.description = customReport.description;
            initializedCustomReport._type = customReport.type;
            initializedCustomReport.configuration = destructureWithOmit(customReport, []);
            initializedCustomReport.configuration._type = customReport.type;

            // Special-case for customReport comprised of customReports or CustomReportSchema with payload_template that may have groups 
            const customReportRequiresRecursiveInitialization = this.customReportRequiresRecursiveInitialization(customReport);
            const customReportSchemaRequiresRecursiveInitialization = this.customReportSchemaRequiresRecursiveInitialization(customReportSchema);

            if (configurationLevel > 1 &&
                (customReportRequiresRecursiveInitialization || customReportSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level customReports that contain customReports not supported.");
            }
            else if (customReportRequiresRecursiveInitialization) {
                throw new Error("CustomReports containing child customReports is not supported.");
            }

        }
        return initializedCustomReport;
    }

    private customReportRequiresRecursiveInitialization(customReport: CustomReport): boolean {
        return CustomReportsTypeGuardService.supportsChildCustomReports(customReport);
    }

    private customReportSchemaRequiresRecursiveInitialization(customReportSchema: CustomReportSchema): boolean {
        return CustomReportsTypeGuardService.supportsChildCustomReports(customReportSchema);
    }
}
